import { Color3, Vector2 } from '@babylonjs/core';
import { Button, Control, StackPanel, TextWrapping } from '@babylonjs/gui/2D';
import { GuiOverlay } from 'Framework/Components/Controls/XView/Overlays';

type ContextMenuButtonType = 'Normal' | 'Image';

interface IContextMenuItem {
    buttonType: ContextMenuButtonType,
    text?: string,
    imageUrl?: string,
    foreground?: Color3,
    background?: Color3,
    tickness?: number,
    isVisible?: boolean,
    id: string
}

interface IContextMenu {
    fontSize?: number,
    fontFamily?: string,
}

export class ContextMenu extends StackPanel {
    public constructor(name: string, guiRenderer: GuiOverlay, options?: IContextMenu) {
        super(name);

        this._guiRenderer = guiRenderer;

        this.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;

        this.style = this._guiRenderer.createStyle();
        this.style.fontFamily = options?.fontFamily ?? this.fontFamily;
        this.style.fontSize = options?.fontSize ?? this.fontSize;

        this.isVisible = false;

        this._guiRenderer.addControl(this);
    }

    public *addMenuItems(...items: Array<IContextMenuItem>): IterableIterator<Control> {
        for (const menuItem of items) {
            const button: Button = this._createButton(menuItem);

            this.addControl(button);

            yield button;
        }

        this.widthInPixels = this._computeMaxWidth();
    }

    public removeMenuItems(items: Array<Control>, dispose: boolean = true): void {
        items.forEach(i => {
            this.removeControl(i)

            if (dispose) {
                i.dispose();
            }
        });
    }

    public showAt(positionX: number, positionY: number): void {
        this._openAt = new Vector2(positionX, positionY);

        this.leftInPixels = this._computeLeftInPixels(positionX);
        this.topInPixels = this._computeTopInPixels(positionY);

        this._setIsOpen(true);
    }

    public close(): void {
        this._setIsOpen(false);
    }

    private _createButton(options: IContextMenuItem): Button {
        let button: Button = null;

        switch (options.buttonType) {
            case 'Normal':
                button = Button.CreateSimpleButton(options.id, options.text);
                break;
            default:
                throw new Error('Not implemented type');
        }

        button.isVisible = options.isVisible ?? true;
        button.thickness = options.tickness ?? 0;
        button.color = (options.foreground ?? Color3.White()).toHexString();
        button.background = (options.background ?? Color3.Black()).toHexString();
        button.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        button.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        button.adaptHeightToChildren = true;
        button.adaptWidthToChildren = true;

        button.textBlock.textWrapping = TextWrapping.WordWrap;
        button.textBlock.resizeToFit = true;
        button.textBlock.paddingTop = 8;
        button.textBlock.paddingBottom = 8;
        button.textBlock.paddingLeft = 10;
        button.textBlock.paddingRight = 10;
        button.textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        button.style = this.style;

        return button;
    }

    private _computeLeftInPixels(desiredLeft: number): number {
        const widthScreen: number = this._guiRenderer.uiTexture.rootContainer.widthInPixels;
        const isWidthOverBoudingScreen: boolean = (desiredLeft + this.widthInPixels) >= widthScreen;

        return isWidthOverBoudingScreen
            ? desiredLeft - this.widthInPixels
            : desiredLeft;
    }

    private _computeTopInPixels(desiredTop: number): number {
        const heightScreen: number = this._guiRenderer.uiTexture.rootContainer.heightInPixels;
        const isHeightOverBoundingScreen: boolean = (desiredTop + this.heightInPixels) >= heightScreen;

        return isHeightOverBoundingScreen
            ? desiredTop - this.heightInPixels
            : desiredTop;
    }

    private _computeMaxWidth(): number {
        const context = this._guiRenderer.uiTexture.getContext();
        context.font = `${this.style.fontSize} ${this.style.fontFamily}`;

        return Math.ceil(Math.max(...this.children.map((c: Button) => (
            context.measureText(c.textBlock.text).width
            + c.textBlock.paddingLeftInPixels
            + c.textBlock.paddingRightInPixels
        ))));
    }

    public get openAt(): Vector2 {
        return this._openAt;
    }

    public get isOpen(): boolean {
        return this._isOpen;
    }

    private _setIsOpen(value: boolean): void {
        this._isOpen = value;
        this.isVisible = value;
    }

    private _openAt: Vector2;
    private _isOpen: boolean;
    private readonly _guiRenderer: GuiOverlay;
}
